import { HourglassLow, X } from "@phosphor-icons/react";
import { Card, Text, Flex, Grid, Tooltip } from "@radix-ui/themes";
import cx from "classnames";
import React, { useState } from "react";

interface TimeSavedBubbleProps {
  timeSaved: number;
  timeReviewTook: number;
  className?: string;
  opacity?: number;
  hoverOpacity?: number;
}

// Utility function to format hours into "X days Y hrs" format
function formatTimeSaved(hours: number): string {
  if (hours < 24) {
    return `${hours} hrs`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (remainingHours === 0) {
    return `${days} day${days > 1 ? "s" : ""}`;
  }

  return `${days} day${days > 1 ? "s" : ""} ${remainingHours} hrs`;
}

// Utility function to format seconds into "X min Y secs" format
function formatTimeReviewTook(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} secs`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (remainingSeconds === 0) {
    return `${minutes} min`;
  }

  return `${minutes} min ${remainingSeconds} secs`;
}

export function TimeSavedBubble({
  timeSaved,
  timeReviewTook,
  className,
  opacity = 0.5,
  hoverOpacity = 1,
}: TimeSavedBubbleProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const currentOpacity = isExpanded ? 1 : isHovered ? hoverOpacity : opacity;

  if (isExpanded) {
    return (
      <Card
        data-testid="time-saved-bubble"
        className={cx(
          "absolute z-[2000] flex items-center gap-2 p-2 transition-opacity duration-200",
          className,
        )}
        style={{ opacity: currentOpacity }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="region"
        aria-label="Time saved information"
      >
        <HourglassLow size={36} className="text-puntt-neutral-gray-6" />
        <Grid>
          <Text size="3" weight="regular">
            You saved{" "}
            <span data-testid="time-saved-value">
              {formatTimeSaved(timeSaved)}
            </span>{" "}
            with this review.
          </Text>
          <Flex gap="2">
            <Text size="2" weight="regular">
              Automated Review:{" "}
              <span data-testid="time-review-took-value">
                {formatTimeReviewTook(timeReviewTook)}
              </span>
            </Text>
            <Text size="2" weight="regular">
              Manual Review: {timeSaved} hrs
            </Text>
          </Flex>
        </Grid>
        <button
          onClick={() => setIsExpanded(false)}
          className="absolute right-1 top-1 rounded-full p-1 transition-colors duration-200 hover:bg-puntt-neutral-gray-4"
          data-testid="time-saved-bubble-close"
          aria-label="Close time saved information"
        >
          <X size={16} className="text-puntt-neutral-gray-11" />
        </button>
      </Card>
    );
  }

  return (
    <Tooltip
      content={
        <Flex direction="column" gap="1">
          <Text size="2">
            You saved {formatTimeSaved(timeSaved)} with this review.
          </Text>
          <Text size="1">
            Automated Review: {formatTimeReviewTook(timeReviewTook)}
          </Text>
          <Text size="1">Manual Review: {timeSaved} hrs</Text>
        </Flex>
      }
    >
      <button
        data-testid="time-saved-timer-button"
        className={cx(
          "bg-white absolute z-[2000] flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-puntt-neutral-gray-6 transition-all duration-200 hover:scale-105",
          className,
        )}
        style={{ opacity: currentOpacity }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsExpanded(true)}
        aria-label={`Show time saved information. You saved ${formatTimeSaved(timeSaved)} with this review. Automated Review: ${formatTimeReviewTook(timeReviewTook)}. Manual Review: ${timeSaved} hrs.`}
      >
        <HourglassLow size={16} className="text-white" />
      </button>
    </Tooltip>
  );
}
